import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { 
  Play, 
  ArrowLeft, 
  ArrowRight,
  Zap,
  Target,
  Award,
  Lightbulb
} from 'lucide-react';

// Helper to shuffle options and update correct index
function shuffleQuestionOptions(question) {
  const options = [...question.options];
  const correctAnswer = options[question.correct];
  for (let i = options.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [options[i], options[j]] = [options[j], options[i]];
  }
  const newCorrect = options.indexOf(correctAnswer);
  return { ...question, options, correct: newCorrect };
}

const TrainingModule = () => {
  const { moduleId: moduleIdParam } = useParams();
  const moduleId = parseInt(moduleIdParam, 10);
  const navigate = useNavigate();
  const { updateModuleProgress, user, loadUser } = useUser();
  const [currentSection, setCurrentSection] = useState(0);
  const [progress, setProgress] = useState(0);
  const [completedSections, setCompletedSections] = useState([]);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(null);
  const [startTime] = useState(Date.now());
  const [timeSpent, setTimeSpent] = useState(0);
  const [shuffledQuestions, setShuffledQuestions] = useState(null);

  // Track time spent on module
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeSpent(Math.floor((Date.now() - startTime) / 1000 / 60));
    }, 60000);

    return () => clearInterval(timer);
  }, [startTime]);

  // Module data for all 10 modules
  const modules = {
    1: {
      id: 1,
      title: "Debate Fundamentals",
      description: "Master the essential building blocks of effective debate including structure, argumentation, and basic techniques.",
      duration: "60 min",
      difficulty: "Beginner",
      xpReward: 200,
      sections: [
        {
          id: 1,
          title: "Introduction to Debate Structure",
          type: "video",
          duration: "10 min",
          content: "Debate is a structured form of argumentation where two or more parties present opposing viewpoints on a specific topic.",
          keyPoints: [
            "Debate involves structured argumentation",
            "Clear organization enhances persuasiveness",
            "Time management is crucial",
            "Respect for opponents is fundamental",
          ]
        },
        {
          id: 2,
          title: "Constructing Basic Arguments",
          type: "interactive",
          duration: "12 min",
          content: "Every argument consists of three essential components: a claim, evidence, and reasoning.",
          examples: [
            "Claim: School uniforms improve academic performance. Evidence: Studies show 15% increase in test scores. Reasoning: Uniforms reduce distractions and create equality.",
            "Claim: Renewable energy is cost-effective. Evidence: Solar panel costs dropped 70% in 10 years. Reasoning: Lower costs make adoption practical.",
          ],
          counter: "Always provide specific evidence and clear reasoning to support your claims."
        },
        {
          id: 3,
          title: "Opening Statements",
          type: "interactive",
          duration: "10 min",
          content: "Opening statements set the tone for your entire argument. They should clearly state your position, preview your main points, and establish credibility.",
          examples: [
            "Good opening: 'Today, I will demonstrate that implementing a four-day workweek benefits both employees and employers through increased productivity, reduced costs, and improved work-life balance.'",
            "Poor opening: 'I think four-day workweeks are good because they're good.'",
          ],
          counter: "Be specific, preview your arguments, and establish your credibility."
        },
        {
          id: 4,
          title: "Evidence Presentation",
          type: "interactive",
          duration: "12 min",
          content: "Evidence is the backbone of any strong argument. Learn how to find, evaluate, and present credible evidence effectively.",
          examples: [
            "Statistical evidence: 'According to the CDC, 95% of COVID-19 deaths occurred in people over 50.'",
            "Expert testimony: 'Dr. Smith, a leading economist, states that this policy will reduce inflation by 2%.'",
            "Historical precedent: 'When similar policies were implemented in Sweden, crime rates decreased by 25%.'",
          ],
          counter: "Use diverse types of evidence and always cite your sources accurately."
        },
        {
          id: 5,
          title: "Basic Rebuttal Techniques",
          type: "interactive",
          duration: "10 min",
          content: "Rebuttal is the process of responding to and countering your opponent's arguments. Learn fundamental techniques to strengthen your position.",
          examples: [
            "Direct refutation: 'My opponent claims X, but the evidence shows Y.'",
            "Concession and refutation: 'While my opponent makes a valid point about A, this doesn't address the larger issue of B.'",
            "Evidence challenge: 'The study my opponent cites has been criticized for its small sample size.'",
          ],
          counter: "Address the strongest points of your opponent's argument, not just the weakest ones."
        },
        {
          id: 6,
          title: "Closing Arguments Quiz",
          type: "quiz",
          duration: "10 min",
          questions: [
            {
              id: 1,
              question: "What are the three essential components of an argument?",
              options: [
                "Fact, opinion, belief",
                "Topic, position, counter",
                "Claim, evidence, reasoning",
                "Introduction, body, conclusion",
              ],
              correct: 2,
              explanation: "Every argument needs a claim (what you're arguing), evidence (supporting facts), and reasoning (why the evidence supports the claim)."
            },
            {
              id: 2,
              question: "What should an opening statement include?",
              options: [
                "Only your position",
                "Just a joke to break the ice",
                "A list of all your evidence",
                "Your position, main points preview, and credibility",
              ],
              correct: 3,
              explanation: "Opening statements should clearly state your position, preview your main arguments, and establish your credibility."
            },
            {
              id: 3,
              question: "Which type of evidence is most persuasive?",
              options: [
                "Vague statistics",
                "Emotional appeals",
                "Credible, relevant, and recent evidence",
                "Personal opinions",
              ],
              correct: 2,
              explanation: "The most persuasive evidence is credible (from reliable sources), relevant (directly supports your claim), and recent (up-to-date)."
            },
            {
              id: 4,
              question: "What is the primary purpose of a rebuttal?",
              options: [
                "To repeat your own arguments",
                "To waste time",
                "To respond to and counter opposing arguments",
                "To insult your opponent",
              ],
              correct: 2,
              explanation: "Rebuttal is the process of responding to and countering your opponent's arguments to strengthen your position."
            },
            {
              id: 5,
              question: "Which of the following is NOT a characteristic of effective evidence?",
              options: [
                "Recent publication date",
                "Credibility of the source",
                "Relevance to the claim",
                "Emotional appeal without facts",
              ],
              correct: 3,
              explanation: "Effective evidence should be relevant, credible, and recent. Emotional appeal without factual support is not effective evidence."
            },
            {
              id: 6,
              question: "What is the difference between a claim and evidence?",
              options: [
                "There is no difference",
                "Evidence is always statistics, claims are opinions",
                "A claim is what you're arguing, evidence supports it",
                "Claims are facts, evidence is optional",
              ],
              correct: 2,
              explanation: "A claim is the position you're arguing for, while evidence provides the facts and data that support your claim."
            },
            {
              id: 7,
              question: "Why is reasoning important in an argument?",
              options: [
                "It connects evidence to claims logically",
                "It confuses opponents",
                "It's required by debate rules",
                "It makes arguments longer",
              ],
              correct: 0,
              explanation: "Reasoning explains how and why your evidence supports your claim, making the logical connection clear."
            },
            {
              id: 8,
              question: "What should you avoid in an opening statement?",
              options: [
                "Stating your position clearly",
                "Establishing credibility",
                "Making vague, unsupported claims",
                "Previewing your main points",
              ],
              correct: 2,
              explanation: "Vague, unsupported claims weaken your opening statement. Be specific and provide a clear roadmap."
            },
          ]
        },
      ]
    },
    2: {
      id: 2,
      title: "Logical Fallacies & Critical Thinking",
      description: "Learn to identify, avoid, and counter common logical fallacies while strengthening your critical thinking skills.",
      duration: "90 min",
      difficulty: "Intermediate",
      xpReward: 300,
      sections: [
        {
          id: 1,
          title: "Introduction to Logical Fallacies",
          type: "video",
          duration: "8 min",
          content: "Logical fallacies are errors in reasoning that can make arguments seem convincing when they're actually flawed.",
          keyPoints: [
            "Logical fallacies undermine argument validity",
            "They can be intentional or unintentional",
            "Recognizing them improves critical thinking",
            "They're common in everyday arguments",
          ]
        },
        {
          id: 2,
          title: "Strawman Fallacy",
          type: "interactive",
          duration: "12 min",
          content: "The strawman fallacy occurs when someone misrepresents an opponent's argument to make it easier to attack.",
          examples: [
            "Person A: 'We should reduce military spending.' Person B: 'So you want to leave our country defenseless?'",
            "Person A: 'I think we should have stricter gun laws.' Person B: 'You want to take away everyone's right to self-defense!'",
          ],
          counter: "Address the actual argument, not a distorted version. Ask for clarification if needed."
        },
        {
          id: 3,
          title: "Ad Hominem Fallacy",
          type: "interactive",
          duration: "10 min",
          content: "Ad hominem attacks target the person making the argument rather than the argument itself.",
          examples: [
            "Person A: 'Climate change is real.' Person B: 'You're just a liberal who doesn't understand economics.'",
            "Person A: 'This policy will help the economy.' Person B: 'You're only saying that because you're rich.'",
          ],
          counter: "Focus on the argument's merits, not the person making it. Separate the argument from the arguer."
        },
        {
          id: 4,
          title: "Slippery Slope Fallacy",
          type: "interactive",
          duration: "10 min",
          content: "The slippery slope fallacy assumes that one action will inevitably lead to a series of increasingly negative consequences.",
          examples: [
            "If we allow same-sex marriage, next people will want to marry animals.",
            "If we raise the minimum wage, businesses will close and unemployment will skyrocket.",
          ],
          counter: "Ask for evidence of the causal chain. Most slippery slope arguments lack evidence for the predicted outcomes."
        },
        {
          id: 5,
          title: "Appeal to Emotion Fallacy",
          type: "interactive",
          duration: "10 min",
          content: "Appeal to emotion uses feelings to manipulate rather than using logical reasoning.",
          examples: [
            "Think of the children! We must ban this video game.",
            "How can you sleep at night knowing people are starving?",
          ],
          counter: "Recognize emotional manipulation and demand logical evidence and reasoning."
        },
        {
          id: 6,
          title: "False Dichotomy Fallacy",
          type: "interactive",
          duration: "10 min",
          content: "False dichotomy presents only two options when more exist, forcing a false choice.",
          examples: [
            "You're either with us or against us.",
            "Either we cut taxes or the economy will collapse.",
          ],
          counter: "Identify additional options and refuse to accept false choices."
        },
        {
          id: 7,
          title: "Circular Reasoning Fallacy",
          type: "interactive",
          duration: "10 min",
          content: "Circular reasoning uses the conclusion as evidence for itself, creating a logical loop.",
          examples: [
            "The Bible is true because it says so in the Bible.",
            "This law is constitutional because it's legal.",
          ],
          counter: "Demand independent evidence that doesn't assume the conclusion."
        },
        {
          id: 8,
          title: "Comprehensive Quiz",
          type: "quiz",
          duration: "15 min",
          questions: [
            {
              id: 1,
              question: "What is a strawman fallacy?",
              options: [
                "Attacking the person instead of the argument",
                "Misrepresenting an opponent's argument to make it easier to attack",
                "Assuming one action will lead to negative consequences",
                "Appealing to authority figures",
              ],
              correct: 1,
              explanation: "A strawman fallacy misrepresents an opponent's argument to make it easier to attack."
            },
            {
              id: 2,
              question: "What should you do when someone uses an ad hominem attack?",
              options: [
                "Focus on the argument's merits, not the person",
                "Ignore them completely",
                "Attack them back personally",
                "Use the same tactic against them",
              ],
              correct: 0,
              explanation: "Focus on the argument's merits and separate the argument from the person making it."
            },
            {
              id: 3,
              question: "What is the problem with slippery slope arguments?",
              options: [
                "They're always wrong",
                "They lack evidence for the predicted causal chain",
                "They're too complex to understand",
                "They're too simple",
              ],
              correct: 1,
              explanation: "Slippery slope arguments typically lack evidence for the predicted chain of consequences."
            },
            {
              id: 4,
              question: "Which fallacy involves presenting only two options when more exist?",
              options: [
                "Appeal to emotion",
                "False dichotomy",
                "Circular reasoning",
                "Strawman fallacy",
              ],
              correct: 1,
              explanation: "False dichotomy presents only two options when more alternatives exist, forcing a false choice."
            },
            {
              id: 5,
              question: "What is circular reasoning?",
              options: [
                "Repeating the same point multiple times",
                "Using the conclusion as evidence for itself",
                "Using circular logic patterns",
                "Making arguments in a circle",
              ],
              correct: 1,
              explanation: "Circular reasoning uses the conclusion as evidence for itself, creating a logical loop."
            },
            {
              id: 6,
              question: "How can you identify an appeal to emotion fallacy?",
              options: [
                "It uses feelings to manipulate instead of logical reasoning",
                "It's always wrong",
                "It's always about sad topics",
                "It always makes you cry",
              ],
              correct: 0,
              explanation: "Appeal to emotion uses feelings to manipulate rather than providing logical evidence and reasoning."
            },
            {
              id: 7,
              question: "What is the best response to a strawman fallacy?",
              options: [
                "Address the actual argument, not the distorted version",
                "Attack the person making it",
                "Use the same tactic back",
                "Ignore it completely",
              ],
              correct: 0,
              explanation: "Address the actual argument, not the distorted version. Ask for clarification if needed."
            },
            {
              id: 8,
              question: "Why are logical fallacies problematic in debate?",
              options: [
                "They make arguments longer",
                "They're always wrong",
                "They're too complex to understand",
                "They undermine argument validity and reasoning",
              ],
              correct: 3,
              explanation: "Logical fallacies undermine argument validity and reasoning, making arguments seem convincing when they're actually flawed."
            },
            {
              id: 9,
              question: "What should you do when you encounter a false dichotomy?",
              options: [
                "Accept the two options given",
                "Choose the better of the two options",
                "Ignore the argument completely",
                "Identify additional options and refuse false choices",
              ],
              correct: 3,
              explanation: "Identify additional options and refuse to accept false choices when more alternatives exist."
            },
            {
              id: 10,
              question: "How can you avoid committing logical fallacies?",
              options: [
                "Never make arguments",
                "Always appeal to authority",
                "Learn to recognize them and use sound reasoning",
                "Use emotional arguments only",
              ],
              correct: 2,
              explanation: "Learn to recognize logical fallacies and use sound reasoning with proper evidence and logic."
            },
          ]
        },
      ]
    },
    3: {
      id: 3,
      title: "Advanced Argumentation",
      description: "Master complex argument structures including deductive and inductive reasoning, syllogisms, and the Toulmin model.",
      duration: "120 min",
      difficulty: "Advanced",
      xpReward: 400,
      sections: [
        {
          id: 1,
          title: "Deductive Reasoning",
          type: "video",
          duration: "12 min",
          content: "Deductive reasoning moves from general principles to specific conclusions, providing certainty when premises are true.",
          keyPoints: [
            "Moves from general to specific",
            "Provides certainty if premises are true",
            "Can be valid or invalid",
            "Uses logical structure",
          ]
        },
        {
          id: 2,
          title: "Inductive Reasoning",
          type: "interactive",
          duration: "12 min",
          content: "Inductive reasoning moves from specific observations to general conclusions, providing probability rather than certainty.",
          examples: [
            "Every swan I've seen is white, so all swans are white.",
            "Most students who study regularly get good grades, so studying improves performance.",
          ],
          counter: "Inductive arguments can be strong or weak based on evidence quality and sample size."
        },
        {
          id: 3,
          title: "Syllogisms",
          type: "interactive",
          duration: "15 min",
          content: "Syllogisms are structured deductive arguments with two premises and a conclusion.",
          examples: [
            "All humans are mortal. Socrates is human. Therefore, Socrates is mortal.",
            "All politicians are liars. John is a politician. Therefore, John is a liar.",
          ],
          counter: "Check for validity and soundness. Valid structure doesn't guarantee true conclusions."
        },
        {
          id: 4,
          title: "Toulmin Model",
          type: "interactive",
          duration: "15 min",
          content: "The Toulmin model provides a framework for analyzing arguments with claim, grounds, warrant, backing, qualifier, and rebuttal.",
          examples: [
            "Claim: School uniforms improve behavior. Grounds: Study shows 30% reduction in discipline issues. Warrant: Uniforms create equality and reduce distractions.",
          ],
          counter: "Use the Toulmin model to strengthen your arguments and identify weaknesses in others."
        },
        {
          id: 5,
          title: "Argument Mapping",
          type: "interactive",
          duration: "12 min",
          content: "Argument mapping visually represents the structure of arguments, showing relationships between claims and evidence.",
          examples: [
            "Main claim → Supporting claim 1 → Evidence A, Evidence B",
            "Main claim → Supporting claim 2 → Evidence C",
          ],
          counter: "Mapping helps identify gaps, redundancies, and logical connections in arguments."
        },
        {
          id: 6,
          title: "Causal Arguments",
          type: "interactive",
          duration: "12 min",
          content: "Causal arguments establish cause-and-effect relationships, requiring careful evidence and reasoning.",
          examples: [
            "Smoking causes lung cancer (established causation)",
            "Education reduces crime rates (correlation vs causation)",
          ],
          counter: "Distinguish between correlation and causation. Look for multiple studies and consider alternatives."
        },
        {
          id: 7,
          title: "Analogical Reasoning",
          type: "interactive",
          duration: "10 min",
          content: "Analogical reasoning compares two things to suggest they're similar in relevant ways.",
          examples: [
            "The government is like a family budget - we can't spend more than we earn.",
            "The internet is like a library - it contains vast information but requires critical evaluation.",
          ],
          counter: "Evaluate whether the analogy is relevant and whether similarities outweigh differences."
        },
        {
          id: 8,
          title: "Statistical Arguments",
          type: "interactive",
          duration: "12 min",
          content: "Statistical arguments use numerical data to support claims, but require careful interpretation.",
          examples: [
            "85% of students prefer online learning (sample size matters)",
            "Average income increased 5% (mean vs median considerations)",
          ],
          counter: "Consider sample size, methodology, and whether statistics are being used appropriately."
        },
        {
          id: 9,
          title: "Expert Testimony",
          type: "interactive",
          duration: "10 min",
          content: "Expert testimony provides authoritative support for arguments when used appropriately.",
          examples: [
            "Dr. Smith, a leading economist, states this policy will reduce inflation by 2%.",
            "According to the CDC, 95% of COVID-19 deaths occurred in people over 50.",
          ],
          counter: "Verify expertise in the specific field and whether the expert has evidence to support their claim."
        },
        {
          id: 10,
          title: "Advanced Argumentation Quiz",
          type: "quiz",
          duration: "15 min",
          questions: [
            {
              id: 1,
              question: "What is the key difference between inductive and deductive reasoning?",
              options: [
                "Deductive uses examples, inductive uses logic",
                "Deductive provides certainty, inductive provides probability",
                "Inductive is more complex than deductive",
                "Inductive is always wrong, deductive is always right",
              ],
              correct: 1,
              explanation: "Deductive reasoning provides certainty if premises are true, while inductive reasoning provides probability based on evidence."
            },
            {
              id: 2,
              question: "What should you be careful about with causal arguments?",
              options: [
                "They're too simple",
                "Confusing correlation with causation",
                "They're too complex",
                "They're always wrong",
              ],
              correct: 1,
              explanation: "Causal arguments require careful evidence to distinguish between correlation and causation."
            },
            {
              id: 3,
              question: "What makes an analogy effective?",
              options: [
                "It's simple",
                "It's complex",
                "The similarities outweigh the differences",
                "It's funny",
              ],
              correct: 2,
              explanation: "Effective analogies have relevant similarities that outweigh the differences between the compared items."
            },
            {
              id: 4,
              question: "What is a syllogism?",
              options: [
                "A structured deductive argument with two premises and a conclusion",
                "A rhetorical device",
                "A type of logical fallacy",
                "A statistical analysis",
              ],
              correct: 0,
              explanation: "A syllogism is a structured deductive argument with two premises and a conclusion."
            },
            {
              id: 5,
              question: "What is the Toulmin model used for?",
              options: [
                "Providing a framework for analyzing arguments",
                "Analyzing logical fallacies",
                "Statistical analysis",
                "Creating analogies",
              ],
              correct: 0,
              explanation: "The Toulmin model provides a framework for analyzing arguments with claim, grounds, warrant, backing, qualifier, and rebuttal."
            },
            {
              id: 6,
              question: "What is argument mapping?",
              options: [
                "Planning debate strategies",
                "Creating maps of debate venues",
                "Visually representing the structure of arguments",
                "Drawing pictures of arguments",
              ],
              correct: 2,
              explanation: "Argument mapping visually represents the structure of arguments, showing relationships between claims and evidence."
            },
            {
              id: 7,
              question: "What should you consider when evaluating statistical arguments?",
              options: [
                "Sample size, methodology, and appropriate use",
                "How impressive they sound",
                "Only the numbers",
                "Whether they support your position",
              ],
              correct: 0,
              explanation: "Consider sample size, methodology, and whether statistics are being used appropriately."
            },
            {
              id: 8,
              question: "What is the difference between validity and soundness in deductive arguments?",
              options: [
                "Soundness is more important than validity",
                "There is no difference",
                "Validity is about truth, soundness is about logic",
                "Validity refers to logical structure, soundness includes true premises",
              ],
              correct: 3,
              explanation: "Validity refers to logical structure, while soundness includes both valid structure and true premises."
            },
            {
              id: 9,
              question: "What is expert testimony?",
              options: [
                "Authoritative support from qualified experts",
                "Testimony in court",
                "Any opinion from someone",
                "Personal experience",
              ],
              correct: 0,
              explanation: "Expert testimony provides authoritative support for arguments when used appropriately from qualified experts."
            },
            {
              id: 10,
              question: "How can you strengthen an inductive argument?",
              options: [
                "Use more emotional language",
                "Repeat the conclusion",
                "Make it longer",
                "Increase sample size and improve evidence quality",
              ],
              correct: 3,
              explanation: "Strengthen inductive arguments by increasing sample size and improving the quality of evidence."
            },
            {
              id: 11,
              question: "What is the purpose of backing in the Toulmin model?",
              options: [
                "To provide emotional appeal",
                "To support the warrant",
                "To make arguments longer",
                "To confuse opponents",
              ],
              correct: 1,
              explanation: "Backing provides additional support for the warrant in the Toulmin model."
            },
            {
              id: 12,
              question: "What should you avoid in analogical reasoning?",
              options: [
                "Irrelevant analogies where differences outweigh similarities",
                "Using any comparisons",
                "Complex examples",
                "Simple comparisons",
              ],
              correct: 0,
              explanation: "Avoid irrelevant analogies where the differences between compared items outweigh the similarities."
            },
          ]
        },
      ]
    },
    4: {
      id: 4,
      title: "Rhetorical Techniques & Persuasion",
      description: "Master the art of persuasive speaking through rhetorical devices, emotional appeals, and audience adaptation.",
      duration: "75 min",
      difficulty: "Intermediate",
      xpReward: 350,
      sections: [
        {
          id: 1,
          title: "Ethos, Pathos, Logos",
          type: "video",
          duration: "12 min",
          content: "Aristotle's three modes of persuasion: ethos (credibility), pathos (emotion), and logos (logic) form the foundation of effective rhetoric.",
          keyPoints: [
            "Ethos establishes speaker credibility",
            "Pathos appeals to emotions",
            "Logos uses logical reasoning",
            "Effective persuasion combines all three",
          ]
        },
        {
          id: 2,
          title: "Rhetorical Questions",
          type: "interactive",
          duration: "10 min",
          content: "Rhetorical questions engage audiences and guide their thinking without requiring answers.",
          examples: [
            "How can we expect students to learn when we underfund their schools?",
            "What kind of society do we want to leave for our children?",
          ],
          counter: "Use rhetorical questions strategically to emphasize points and engage audiences."
        },
        {
          id: 3,
          title: "Repetition and Parallelism",
          type: "interactive",
          duration: "12 min",
          content: "Repetition and parallelism create rhythm, emphasis, and memorable phrases.",
          examples: [
            "I have a dream that one day... I have a dream that one day... I have a dream that one day...",
            "We will fight on the beaches, we will fight on the landing grounds, we will fight in the fields.",
          ],
          counter: "Use repetition purposefully to emphasize key points and create memorable phrases."
        },
        {
          id: 4,
          title: "Analogies and Metaphors",
          type: "interactive",
          duration: "12 min",
          content: "Analogies and metaphors make complex ideas accessible and memorable through comparison.",
          examples: [
            "Life is like a box of chocolates - you never know what you're gonna get.",
            "The internet is a vast ocean of information.",
          ],
          counter: "Choose analogies that are relevant and help clarify your point."
        },
        {
          id: 5,
          title: "Audience Analysis",
          type: "interactive",
          duration: "12 min",
          content: "Understanding your audience helps you adapt your message and choose appropriate rhetorical strategies.",
          examples: [
            "For experts: Use technical language and detailed evidence",
            "For general audience: Use accessible language and relatable examples",
          ],
          counter: "Always consider who you're speaking to and adapt accordingly."
        },
        {
          id: 6,
          title: "Emotional Appeals",
          type: "interactive",
          duration: "10 min",
          content: "Emotional appeals can be powerful when used ethically and appropriately.",
          examples: [
            "Appeal to hope: 'Together, we can build a better future.'",
            "Appeal to fear: 'If we don't act now, the consequences will be devastating.'",
          ],
          counter: "Use emotional appeals ethically and balance them with logical reasoning."
        },
        {
          id: 7,
          title: "Rhetorical Techniques Quiz",
          type: "quiz",
          duration: "12 min",
          questions: [
            {
              id: 1,
              question: "What are Aristotle's three modes of persuasion?",
              options: [
                "Ethos, pathos, logos",
                "Argument, appeal, authority",
                "Logic, emotion, credibility",
                "Reason, feeling, trust",
              ],
              correct: 0,
              explanation: "Aristotle identified ethos (credibility), pathos (emotion), and logos (logic) as the three modes of persuasion."
            },
            {
              id: 2,
              question: "What is the purpose of rhetorical questions?",
              options: [
                "To fill time",
                "To get actual answers",
                "To confuse opponents",
                "To engage audiences and guide thinking",
              ],
              correct: 3,
              explanation: "Rhetorical questions engage audiences and guide their thinking without requiring answers."
            },
            {
              id: 3,
              question: "Why is audience analysis important?",
              options: [
                "It's not important",
                "It takes too much time",
                "It makes you nervous",
                "It helps you adapt your message appropriately",
              ],
              correct: 3,
              explanation: "Understanding your audience helps you adapt your message and choose appropriate rhetorical strategies."
            },
            {
              id: 4,
              question: "What is the purpose of repetition and parallelism?",
              options: [
                "To fill time",
                "To create rhythm, emphasis, and memorable phrases",
                "To make speeches longer",
                "To confuse the audience",
              ],
              correct: 1,
              explanation: "Repetition and parallelism create rhythm, emphasis, and memorable phrases."
            },
            {
              id: 5,
              question: "How do analogies and metaphors help in persuasion?",
              options: [
                "They make complex ideas accessible and memorable",
                "They confuse opponents",
                "They make arguments longer",
                "They're required by debate rules",
              ],
              correct: 0,
              explanation: "Analogies and metaphors make complex ideas accessible and memorable through comparison."
            },
            {
              id: 6,
              question: "What should you consider when using emotional appeals?",
              options: [
                "Use them ethically and balance with logical reasoning",
                "Avoid them completely",
                "Use them as much as possible",
                "Only use them for sad topics",
              ],
              correct: 0,
              explanation: "Use emotional appeals ethically and balance them with logical reasoning."
            },
            {
              id: 7,
              question: "What is ethos in rhetoric?",
              options: [
                "Evidence presentation",
                "Logical reasoning",
                "Speaker credibility and character",
                "Emotional appeal",
              ],
              correct: 2,
              explanation: "Ethos refers to speaker credibility and character in rhetorical persuasion."
            },
            {
              id: 8,
              question: "What is pathos in rhetoric?",
              options: [
                "Logical reasoning",
                "Speaker credibility",
                "Emotional appeal",
                "Evidence presentation",
              ],
              correct: 2,
              explanation: "Pathos refers to emotional appeal in rhetorical persuasion."
            },
            {
              id: 9,
              question: "What is logos in rhetoric?",
              options: [
                "Logical reasoning and evidence",
                "Audience engagement",
                "Speaker credibility",
                "Emotional appeal",
              ],
              correct: 0,
              explanation: "Logos refers to logical reasoning and evidence in rhetorical persuasion."
            },
            {
              id: 10,
              question: "How should you adapt your message for different audiences?",
              options: [
                "Always use simple language",
                "Use the same approach for everyone",
                "Always use technical language",
                "Consider their background, knowledge, and values",
              ],
              correct: 3,
              explanation: "Consider the audience's background, knowledge, and values when adapting your message."
            },
          ]
        },
      ]
    },
    5: {
      id: 5,
      title: "Rebuttal & Cross-Examination",
      description: "Learn advanced techniques for effectively countering arguments and conducting powerful cross-examinations.",
      duration: "100 min",
      difficulty: "Advanced",
      xpReward: 450,
      sections: [
        {
          id: 1,
          title: "Direct Refutation",
          type: "video",
          duration: "12 min",
          content: "Direct refutation involves addressing and countering opposing arguments with precision and effectiveness.",
          keyPoints: [
            "Identify the core claim of the opponent",
            "Provide counter-evidence",
            "Explain why their argument fails",
            "Maintain respect while being firm",
          ]
        },
        {
          id: 2,
          title: "Concession and Refutation",
          type: "interactive",
          duration: "12 min",
          content: "Concession and refutation acknowledges valid points while still advancing your position.",
          examples: [
            "While my opponent makes a valid point about cost, this doesn't address the long-term benefits.",
            "I agree that this policy has some drawbacks, but the benefits far outweigh them.",
          ],
          counter: "Acknowledge valid points to build credibility while still advancing your position."
        },
        {
          id: 3,
          title: "Cross-Examination Techniques",
          type: "interactive",
          duration: "15 min",
          content: "Cross-examination is an opportunity to expose weaknesses in your opponent's case through strategic questioning.",
          examples: [
            "Ask yes/no questions to limit responses",
            "Use leading questions to guide answers",
            "Ask for specific evidence and examples",
          ],
          counter: "Prepare questions in advance and listen carefully to responses."
        },
        {
          id: 4,
          title: "Evidence Challenges",
          type: "interactive",
          duration: "12 min",
          content: "Challenging evidence involves questioning the credibility, relevance, or interpretation of your opponent's evidence.",
          examples: [
            "Question the source's credibility",
            "Challenge the methodology of studies",
            "Point out outdated or irrelevant evidence",
          ],
          counter: "Focus on the evidence itself, not the person presenting it."
        },
        {
          id: 5,
          title: "Witness Preparation",
          type: "interactive",
          duration: "10 min",
          content: "Preparing witnesses for cross-examination helps them present their testimony effectively.",
          examples: [
            "Practice answering difficult questions",
            "Prepare for potential challenges",
            "Review key evidence and facts",
          ],
          counter: "Help witnesses understand their role and prepare for challenges."
        },
        {
          id: 6,
          title: "Leading Questions",
          type: "interactive",
          duration: "10 min",
          content: "Leading questions guide witnesses toward desired answers while maintaining control.",
          examples: [
            "Isn't it true that you were present at the meeting?",
            "Wouldn't you agree that this evidence supports our position?",
          ],
          counter: "Use leading questions strategically to advance your case."
        },
        {
          id: 7,
          title: "Impeachment Strategies",
          type: "interactive",
          duration: "12 min",
          content: "Impeachment involves challenging a witness's credibility or the reliability of their testimony.",
          examples: [
            "Show prior inconsistent statements",
            "Question qualifications or expertise",
            "Demonstrate bias or motive",
          ],
          counter: "Use impeachment carefully and with strong evidence."
        },
        {
          id: 8,
          title: "Rebuttal Organization",
          type: "interactive",
          duration: "12 min",
          content: "Organizing your rebuttal effectively maximizes its impact and clarity.",
          examples: [
            "Address strongest arguments first",
            "Group related points together",
            "End with your strongest counter-argument",
          ],
          counter: "Structure your rebuttal for maximum impact and clarity."
        },
        {
          id: 9,
          title: "Rebuttal & Cross-Examination Quiz",
          type: "quiz",
          duration: "12 min",
          questions: [
            {
              id: 1,
              question: "What is the purpose of concession and refutation?",
              options: [
                "To agree with everything your opponent says",
                "To confuse your opponent",
                "To waste time",
                "To acknowledge valid points while advancing your position",
              ],
              correct: 3,
              explanation: "Concession and refutation acknowledges valid points to build credibility while still advancing your position."
            },
            {
              id: 2,
              question: "What should you focus on when challenging evidence?",
              options: [
                "How long it took to find",
                "How expensive it was",
                "The evidence itself - credibility, relevance, methodology",
                "The person presenting it",
              ],
              correct: 2,
              explanation: "Focus on the evidence itself - its credibility, relevance, and methodology - not the person presenting it."
            },
            {
              id: 3,
              question: "What is the purpose of cross-examination?",
              options: [
                "To be mean to your opponent",
                "To waste time",
                "To show off",
                "To expose weaknesses in your opponent's case",
              ],
              correct: 3,
              explanation: "Cross-examination is an opportunity to expose weaknesses in your opponent's case through strategic questioning."
            },
            {
              id: 4,
              question: "What are leading questions used for?",
              options: [
                "To waste time",
                "To be rude",
                "To confuse witnesses",
                "To guide witnesses toward desired answers",
              ],
              correct: 3,
              explanation: "Leading questions guide witnesses toward desired answers while maintaining control."
            },
            {
              id: 5,
              question: "What is impeachment in debate?",
              options: [
                "Removing a president",
                "Challenging a witness's credibility or testimony reliability",
                "Ending the debate early",
                "Attacking your opponent personally",
              ],
              correct: 1,
              explanation: "Impeachment involves challenging a witness's credibility or the reliability of their testimony."
            },
            {
              id: 6,
              question: "How should you organize your rebuttal?",
              options: [
                "Address arguments in order mentioned",
                "Address strongest arguments first, group related points, end with strongest counter",
                "Address weakest arguments first",
                "Randomly",
              ],
              correct: 1,
              explanation: "Organize rebuttal by addressing strongest arguments first, grouping related points, and ending with your strongest counter-argument."
            },
            {
              id: 7,
              question: "What is direct refutation?",
              options: [
                "Ignoring your opponent's points",
                "Addressing and countering opposing arguments with precision",
                "Attacking your opponent personally",
                "Repeating your own arguments",
              ],
              correct: 1,
              explanation: "Direct refutation involves addressing and countering opposing arguments with precision and effectiveness."
            },
            {
              id: 8,
              question: "What should you do when preparing witnesses?",
              options: [
                "Make them nervous",
                "Tell them exactly what to say",
                "Ignore them completely",
                "Help them understand their role and prepare for challenges",
              ],
              correct: 3,
              explanation: "Help witnesses understand their role and prepare for potential challenges during cross-examination."
            },
            {
              id: 9,
              question: "What is the best approach to cross-examination questions?",
              options: [
                "Ask rhetorical questions",
                "Ask complex questions",
                "Ask open-ended questions",
                "Ask yes/no questions to limit responses",
              ],
              correct: 3,
              explanation: "Ask yes/no questions to limit responses and maintain control during cross-examination."
            },
            {
              id: 10,
              question: "Why is witness preparation important?",
              options: [
                "It's not important",
                "It helps witnesses present testimony effectively under pressure",
                "It makes them nervous",
                "It takes too much time",
              ],
              correct: 1,
              explanation: "Witness preparation helps them present their testimony effectively under the pressure of cross-examination."
            },
          ]
        },
      ]
    },
    6: {
      id: 6,
      title: "Debate Formats & Rules",
      description: "Comprehensive guide to major debate formats including Lincoln-Douglas, Parliamentary, Policy, and Public Forum.",
      duration: "150 min",
      difficulty: "Intermediate",
      xpReward: 500,
      sections: [
        {
          id: 1,
          title: "Lincoln-Douglas Format",
          type: "video",
          duration: "15 min",
          content: "Lincoln-Douglas debate focuses on values and philosophy, with one-on-one competition and emphasis on moral reasoning.",
          keyPoints: [
            "One-on-one competition",
            "Focus on values and philosophy",
            "Emphasis on moral reasoning",
            "Structured time format",
          ]
        },
        {
          id: 2,
          title: "Parliamentary Debate",
          type: "interactive",
          duration: "12 min",
          content: "Parliamentary debate simulates British parliamentary procedure with impromptu topics and team competition.",
          examples: [
            "Government vs Opposition teams",
            "Impromptu topic announcement",
            "Limited preparation time",
            "Emphasis on rhetoric and persuasion",
          ],
          counter: "Adapt quickly to new topics and work effectively with your partner."
        },
        {
          id: 3,
          title: "Policy Debate",
          type: "interactive",
          duration: "15 min",
          content: "Policy debate involves detailed analysis of policy proposals with extensive evidence and technical arguments.",
          examples: [
            "Affirmative proposes policy change",
            "Negative defends status quo",
            "Extensive evidence requirements",
            "Technical argumentation",
          ],
          counter: "Develop strong research skills and understand policy implications deeply."
        },
        {
          id: 4,
          title: "Public Forum",
          type: "interactive",
          duration: "12 min",
          content: "Public Forum debate emphasizes accessibility and public speaking skills for general audiences.",
          examples: [
            "Accessible language and arguments",
            "Emphasis on public speaking",
            "Team competition",
            "Current events focus",
          ],
          counter: "Make complex arguments accessible to general audiences."
        },
        {
          id: 5,
          title: "IPDA Format",
          type: "interactive",
          duration: "10 min",
          content: "International Public Debate Association format emphasizes persuasion and audience adaptation.",
          examples: [
            "Audience voting determines winner",
            "Emphasis on persuasion over technical argument",
            "Accessible language required",
            "Interactive format",
          ],
          counter: "Focus on persuading the audience rather than technical debate skills."
        },
        {
          id: 6,
          title: "World Schools",
          type: "interactive",
          duration: "12 min",
          content: "World Schools format combines prepared and impromptu debates with international standards.",
          examples: [
            "Three speakers per team",
            "Prepared and impromptu rounds",
            "International competition format",
            "Emphasis on rhetoric and style",
          ],
          counter: "Develop both prepared and impromptu speaking skills."
        },
        {
          id: 7,
          title: "Congressional Debate",
          type: "interactive",
          duration: "12 min",
          content: "Congressional debate simulates legislative procedure with bills and resolutions.",
          examples: [
            "Legislative simulation",
            "Bills and resolutions",
            "Parliamentary procedure",
            "Multiple speakers on same topic",
          ],
          counter: "Understand parliamentary procedure and legislative processes."
        },
        {
          id: 8,
          title: "Extemporaneous Speaking",
          type: "interactive",
          duration: "10 min",
          content: "Extemporaneous speaking involves preparing and delivering speeches on current events with limited preparation time.",
          examples: [
            "30-minute preparation time",
            "Current events focus",
            "Single speaker format",
            "Emphasis on organization and delivery",
          ],
          counter: "Stay informed about current events and develop quick organization skills."
        },
        {
          id: 9,
          title: "Format-Specific Strategies",
          type: "interactive",
          duration: "15 min",
          content: "Each debate format requires specific strategies and approaches for success.",
          examples: [
            "LD: Focus on value frameworks and moral reasoning",
            "Policy: Develop strong evidence and technical skills",
            "PF: Emphasize accessibility and public speaking",
          ],
          counter: "Adapt your approach to the specific format requirements."
        },
        {
          id: 10,
          title: "Time Structures",
          type: "interactive",
          duration: "12 min",
          content: "Understanding time structures is crucial for effective debate performance.",
          examples: [
            "LD: 6-3-7-3-4-6-3",
            "Policy: 8-3-8-3-5-5-5-5",
            "PF: 4-4-3-3-2-2-2-2",
          ],
          counter: "Practice within time limits and develop time management skills."
        },
        {
          id: 11,
          title: "Judging Criteria",
          type: "interactive",
          duration: "10 min",
          content: "Understanding how you'll be judged helps you adapt your arguments and presentation.",
          examples: [
            "Content: Quality of arguments and evidence",
            "Delivery: Speaking skills and presentation",
            "Strategy: Overall approach and organization",
          ],
          counter: "Adapt your approach based on judging criteria and preferences."
        },
        {
          id: 12,
          title: "Debate Formats Quiz",
          type: "quiz",
          duration: "15 min",
          questions: [
            {
              id: 1,
              question: "Which format focuses on values and philosophy?",
              options: [
                "Parliamentary",
                "Public Forum",
                "Policy Debate",
                "Lincoln-Douglas",
              ],
              correct: 3,
              explanation: "Lincoln-Douglas debate focuses on values and philosophy with emphasis on moral reasoning."
            },
            {
              id: 2,
              question: "What is unique about Public Forum debate?",
              options: [
                "It's always one-on-one",
                "It emphasizes accessibility for general audiences",
                "It's only for experts",
                "It requires extensive evidence",
              ],
              correct: 1,
              explanation: "Public Forum emphasizes accessibility and public speaking skills for general audiences."
            },
            {
              id: 3,
              question: "Why is understanding time structures important?",
              options: [
                "It makes you nervous",
                "It's just a formality",
                "It's not important",
                "It helps with time management and effective presentation",
              ],
              correct: 3,
              explanation: "Understanding time structures helps with time management and effective presentation within limits."
            },
            {
              id: 4,
              question: "What characterizes Parliamentary debate?",
              options: [
                "Extensive evidence requirements",
                "Legislative simulation",
                "One-on-one competition",
                "Impromptu topics and team competition",
              ],
              correct: 3,
              explanation: "Parliamentary debate features impromptu topics and team competition simulating British parliamentary procedure."
            },
            {
              id: 5,
              question: "What is the focus of Policy debate?",
              options: [
                "Values and philosophy",
                "Accessibility for general audiences",
                "Legislative procedure",
                "Detailed analysis of policy proposals",
              ],
              correct: 3,
              explanation: "Policy debate involves detailed analysis of policy proposals with extensive evidence and technical arguments."
            },
            {
              id: 6,
              question: "What is unique about IPDA format?",
              options: [
                "Extensive evidence requirements",
                "Legislative simulation",
                "One-on-one competition",
                "Audience voting determines winner",
              ],
              correct: 3,
              explanation: "IPDA format features audience voting to determine the winner, emphasizing persuasion over technical argument."
            },
            {
              id: 7,
              question: "What characterizes World Schools format?",
              options: [
                "Legislative simulation",
                "Audience voting",
                "One-on-one competition",
                "Three speakers per team with prepared and impromptu rounds",
              ],
              correct: 3,
              explanation: "World Schools format features three speakers per team with both prepared and impromptu debate rounds."
            },
            {
              id: 8,
              question: "What is Congressional debate?",
              options: [
                "Legislative simulation with bills and resolutions",
                "Debate about Congress",
                "Policy debate format",
                "Parliamentary procedure",
              ],
              correct: 0,
              explanation: "Congressional debate simulates legislative procedure with bills and resolutions."
            },
            {
              id: 9,
              question: "What is Extemporaneous speaking?",
              options: [
                "Debate about extemporaneous topics",
                "Speaking without preparation",
                "Impromptu debate",
                "Preparing and delivering speeches on current events with limited time",
              ],
              correct: 3,
              explanation: "Extemporaneous speaking involves preparing and delivering speeches on current events with limited preparation time."
            },
            {
              id: 10,
              question: "What should you consider when adapting to different formats?",
              options: [
                "Use the same approach for all formats",
                "Always use technical language",
                "Adapt your strategy to format-specific requirements",
                "Ignore format differences",
              ],
              correct: 2,
              explanation: "Adapt your approach to the specific format requirements and judging criteria."
            },
            {
              id: 11,
              question: "What is the purpose of judging criteria?",
              options: [
                "To create rules",
                "To help you adapt your arguments and presentation",
                "To make debates longer",
                "To confuse debaters",
              ],
              correct: 1,
              explanation: "Understanding judging criteria helps you adapt your arguments and presentation to meet expectations."
            },
            {
              id: 12,
              question: "How do format-specific strategies differ?",
              options: [
                "All formats use the same strategy",
                "Each format requires different approaches and skills",
                "They don't differ",
                "Only advanced formats have strategies",
              ],
              correct: 1,
              explanation: "Each debate format requires different approaches and skills based on its unique characteristics and requirements."
            },
          ]
        },
      ]
    },
    7: {
      id: 7,
      title: "Evidence & Research",
      description: "Master the art of finding, evaluating, and presenting credible evidence to strengthen your arguments.",
      duration: "80 min",
      difficulty: "Intermediate",
      xpReward: 350,
      sections: [
        {
          id: 1,
          title: "Source Evaluation",
          type: "video",
          duration: "12 min",
          content: "Evaluating sources is crucial for building credible arguments. Learn to assess authority, accuracy, and relevance.",
          keyPoints: [
            "Check author credentials and expertise",
            "Evaluate publication quality",
            "Assess accuracy and reliability",
            "Consider relevance to your argument",
          ]
        },
        {
          id: 2,
          title: "Evidence Hierarchy",
          type: "interactive",
          duration: "10 min",
          content: "Different types of evidence have varying levels of credibility and persuasiveness.",
          examples: [
            "Primary sources: Original documents, eyewitness accounts",
            "Secondary sources: Analysis and interpretation of primary sources",
            "Tertiary sources: Encyclopedias, textbooks",
          ],
          counter: "Use the highest quality evidence available and acknowledge limitations."
        },
        {
          id: 3,
          title: "Citation Methods",
          type: "interactive",
          duration: "12 min",
          content: "Proper citation gives credit to sources and allows others to verify your evidence.",
          examples: [
            "MLA format: Author, Title, Publisher, Year",
            "APA format: Author, Year, Title, Publisher",
            "Chicago style: Author, Title, Publisher, Year",
          ],
          counter: "Choose appropriate citation style and be consistent throughout your argument."
        },
        {
          id: 4,
          title: "Research Strategies",
          type: "interactive",
          duration: "12 min",
          content: "Effective research requires systematic approaches to finding and organizing information.",
          examples: [
            "Start with general sources, then narrow down",
            "Use multiple search terms and databases",
            "Keep organized notes and track sources",
            "Evaluate information as you find it",
          ],
          counter: "Develop a systematic research process and stay organized."
        },
        {
          id: 5,
          title: "Database Searching",
          type: "interactive",
          duration: "10 min",
          content: "Academic databases provide access to high-quality, peer-reviewed sources.",
          examples: [
            "Use Boolean operators (AND, OR, NOT)",
            "Use quotation marks for exact phrases",
            "Use wildcards for variations",
            "Limit by date, publication type, or subject",
          ],
          counter: "Learn to use advanced search techniques to find relevant sources efficiently."
        },
        {
          id: 6,
          title: "Fact-Checking Techniques",
          type: "interactive",
          duration: "12 min",
          content: "Fact-checking ensures the accuracy of your evidence and strengthens your credibility.",
          examples: [
            "Cross-reference multiple sources",
            "Check original sources when possible",
            "Verify statistics and data",
            "Look for retractions or corrections",
          ],
          counter: "Always verify facts before using them in arguments."
        },
        {
          id: 7,
          title: "Statistical Analysis",
          type: "interactive",
          duration: "12 min",
          content: "Understanding basic statistical concepts helps you evaluate and present numerical evidence effectively.",
          examples: [
            "Mean vs median vs mode",
            "Correlation vs causation",
            "Sample size and margin of error",
            "Statistical significance",
          ],
          counter: "Understand the limitations and proper interpretation of statistical evidence."
        },
        {
          id: 8,
          title: "Evidence & Research Quiz",
          type: "quiz",
          duration: "12 min",
          questions: [
            {
              id: 1,
              question: "What is the highest level of evidence in the hierarchy?",
              options: [
                "Secondary sources",
                "Wikipedia articles",
                "Primary sources",
                "Tertiary sources",
              ],
              correct: 2,
              explanation: "Primary sources are original documents and eyewitness accounts, making them the highest level of evidence."
            },
            {
              id: 2,
              question: "Why is proper citation important?",
              options: [
                "It's just a formality",
                "It gives credit to sources and allows verification",
                "It's required by law",
                "It makes your argument longer",
              ],
              correct: 1,
              explanation: "Proper citation gives credit to sources and allows others to verify your evidence."
            },
            {
              id: 3,
              question: "What should you do when fact-checking?",
              options: [
                "Cross-reference multiple sources and verify facts",
                "Only check facts you disagree with",
                "Trust the first source you find",
                "Ignore facts that are inconvenient",
              ],
              correct: 0,
              explanation: "Fact-checking involves cross-referencing multiple sources and verifying facts before using them."
            },
            {
              id: 4,
              question: "What should you evaluate when assessing source credibility?",
              options: [
                "Whether you agree with it",
                "How recent the source is",
                "Only the author's name",
                "Author credentials, publication quality, accuracy, and relevance",
              ],
              correct: 3,
              explanation: "Evaluate author credentials, publication quality, accuracy, and relevance when assessing source credibility."
            },
            {
              id: 5,
              question: "What are Boolean operators used for?",
              options: [
                "Making searches longer",
                "Making searches more complex",
                "Confusing search engines",
                "Refining database searches with AND, OR, NOT",
              ],
              correct: 3,
              explanation: "Boolean operators (AND, OR, NOT) help refine database searches to find more relevant sources."
            },
            {
              id: 6,
              question: "What should you consider when evaluating statistical evidence?",
              options: [
                "Only the numbers",
                "How impressive the statistics sound",
                "Whether they support your argument",
                "Sample size, methodology, and appropriate interpretation",
              ],
              correct: 3,
              explanation: "Consider sample size, methodology, and whether statistics are being interpreted appropriately."
            },
            {
              id: 7,
              question: "What is the difference between correlation and causation?",
              options: [
                "There is no difference",
                "Correlation shows relationship, causation shows cause-and-effect",
                "Correlation is always wrong",
                "Causation is always right",
              ],
              correct: 1,
              explanation: "Correlation shows a relationship between variables, while causation shows a cause-and-effect relationship."
            },
            {
              id: 8,
              question: "Why is source evaluation crucial?",
              options: [
                "It helps build credible arguments with reliable evidence",
                "It's required by teachers",
                "It makes research take longer",
                "It's not important",
              ],
              correct: 0,
              explanation: "Source evaluation helps build credible arguments by ensuring you use reliable and relevant evidence."
            },
          ]
        },
      ]
    },
    8: {
      id: 8,
      title: "Public Speaking & Delivery",
      description: "Develop powerful speaking skills including voice projection, body language, and audience engagement.",
      duration: "90 min",
      difficulty: "Intermediate",
      xpReward: 400,
      sections: [
        {
          id: 1,
          title: "Voice Projection",
          type: "video",
          duration: "12 min",
          content: "Voice projection ensures your message reaches your audience clearly and effectively.",
          keyPoints: [
            "Breathe from your diaphragm",
            "Speak from your chest, not your throat",
            "Project to the back of the room",
            "Vary your volume for emphasis",
          ]
        },
        {
          id: 2,
          title: "Body Language",
          type: "interactive",
          duration: "12 min",
          content: "Your body language communicates confidence, engagement, and credibility to your audience.",
          examples: [
            "Stand with feet shoulder-width apart",
            "Use open gestures",
            "Maintain good posture",
            "Avoid nervous movements",
          ],
          counter: "Practice confident body language to enhance your presentation."
        },
        {
          id: 3,
          title: "Pacing and Rhythm",
          type: "interactive",
          duration: "12 min",
          content: "Effective pacing helps maintain audience attention and emphasizes key points.",
          examples: [
            "Slow down for important points",
            "Use pauses for emphasis",
            "Vary your speaking rate",
            "Match pace to content complexity",
          ],
          counter: "Use pacing strategically to enhance your message."
        },
        {
          id: 4,
          title: "Audience Engagement",
          type: "interactive",
          duration: "12 min",
          content: "Engaging your audience keeps them interested and makes your message more memorable.",
          examples: [
            "Ask rhetorical questions",
            "Use relatable examples",
            "Make eye contact",
            "Respond to audience reactions",
          ],
          counter: "Actively work to maintain audience attention and interest."
        },
        {
          id: 5,
          title: "Confidence Building",
          type: "interactive",
          duration: "12 min",
          content: "Building confidence improves your delivery and helps you connect with your audience.",
          examples: [
            "Practice extensively",
            "Visualize success",
            "Focus on your message, not yourself",
            "Use positive self-talk",
          ],
          counter: "Confidence comes from preparation and focusing on your audience's needs."
        },
        {
          id: 6,
          title: "Eye Contact",
          type: "interactive",
          duration: "10 min",
          content: "Eye contact creates connection and shows confidence and engagement.",
          examples: [
            "Make eye contact with different audience members",
            "Hold eye contact for 3-5 seconds",
            "Include the entire audience",
            "Don't stare at one person",
          ],
          counter: "Use eye contact to build rapport and show confidence."
        },
        {
          id: 7,
          title: "Gestures and Movement",
          type: "interactive",
          duration: "12 min",
          content: "Purposeful gestures and movement enhance your message and maintain audience attention.",
          examples: [
            "Use gestures to emphasize points",
            "Move purposefully around the space",
            "Avoid distracting movements",
            "Match gestures to your message",
          ],
          counter: "Use movement and gestures to enhance, not distract from, your message."
        },
        {
          id: 8,
          title: "Public Speaking Quiz",
          type: "quiz",
          duration: "12 min",
          questions: [
            {
              id: 1,
              question: "What is the best way to project your voice?",
              options: [
                "Yell at the audience",
                "Speak louder from your throat",
                "Breathe from your diaphragm and speak from your chest",
                "Use a microphone always",
              ],
              correct: 2,
              explanation: "Proper voice projection involves breathing from your diaphragm and speaking from your chest, not your throat."
            },
            {
              id: 2,
              question: "How should you use eye contact?",
              options: [
                "Make eye contact with different audience members for 3-5 seconds each",
                "Avoid eye contact to reduce nervousness",
                "Only look at friendly faces",
                "Stare at one person the whole time",
              ],
              correct: 0,
              explanation: "Effective eye contact involves making contact with different audience members for 3-5 seconds each."
            },
            {
              id: 3,
              question: "What is the best way to build confidence?",
              options: [
                "Focus on yourself and your nervousness",
                "Practice extensively and focus on your message",
                "Avoid practicing to stay fresh",
                "Think about what could go wrong",
              ],
              correct: 1,
              explanation: "Confidence comes from extensive practice and focusing on your message and audience, not yourself."
            },
            {
              id: 4,
              question: "What is the purpose of body language in public speaking?",
              options: [
                "To follow rules",
                "To communicate confidence, engagement, and credibility",
                "To fill space",
                "To look professional",
              ],
              correct: 1,
              explanation: "Body language communicates confidence, engagement, and credibility to your audience."
            },
            {
              id: 5,
              question: "How should you use pacing in your speech?",
              options: [
                "Speak as fast as possible",
                "Speak as slow as possible",
                "Speak at the same speed throughout",
                "Vary your pace to emphasize key points and maintain attention",
              ],
              correct: 3,
              explanation: "Vary your pace to emphasize key points and maintain audience attention."
            },
            {
              id: 6,
              question: "What is the purpose of audience engagement?",
              options: [
                "To show off",
                "To fill time",
                "To keep audiences interested and make messages memorable",
                "To make speeches longer",
              ],
              correct: 2,
              explanation: "Audience engagement keeps listeners interested and makes your message more memorable."
            },
            {
              id: 7,
              question: "How should you use gestures and movement?",
              options: [
                "Stand still and don't move",
                "Move around randomly",
                "Use purposeful gestures and movement to enhance your message",
                "Use as many gestures as possible",
              ],
              correct: 2,
              explanation: "Use purposeful gestures and movement to enhance, not distract from, your message."
            },
            {
              id: 8,
              question: "What should you avoid in public speaking?",
              options: [
                "Distracting movements and nervous habits",
                "Using any gestures",
                "Making eye contact",
                "Making any mistakes",
              ],
              correct: 0,
              explanation: "Avoid distracting movements and nervous habits that can undermine your presentation."
            },
            {
              id: 9,
              question: "What is the purpose of pauses in speech?",
              options: [
                "To confuse the audience",
                "To emphasize points and create impact",
                "To show nervousness",
                "To fill time",
              ],
              correct: 1,
              explanation: "Pauses emphasize points and create impact, making your message more powerful."
            },
            {
              id: 10,
              question: "How should you respond to audience reactions?",
              options: [
                "Acknowledge and adapt to audience responses",
                "Stop speaking if they react",
                "Ignore them completely",
                "Get angry if they don't respond",
              ],
              correct: 0,
              explanation: "Acknowledge and adapt to audience responses to maintain engagement and connection."
            },
          ]
        },
      ]
    },
    9: {
      id: 9,
      title: "Ethics & Sportsmanship",
      description: "Learn the ethical principles of debate, fair play, and maintaining integrity in competitive environments.",
      duration: "45 min",
      difficulty: "Beginner",
      xpReward: 200,
      sections: [
        {
          id: 1,
          title: "Debate Ethics",
          type: "video",
          duration: "8 min",
          content: "Ethical debate requires honesty, respect, and commitment to truth-seeking.",
          keyPoints: [
            "Present arguments honestly",
            "Respect opponents and judges",
            "Seek truth, not just victory",
            "Maintain academic integrity",
          ]
        },
        {
          id: 2,
          title: "Fair Play Principles",
          type: "interactive",
          duration: "8 min",
          content: "Fair play ensures that debate remains a constructive and educational activity.",
          examples: [
            "Follow time limits and rules",
            "Don't misrepresent evidence",
            "Respect preparation time",
            "Accept decisions gracefully",
          ],
          counter: "Fair play benefits everyone and maintains the integrity of debate."
        },
        {
          id: 3,
          title: "Sportsmanship",
          type: "interactive",
          duration: "8 min",
          content: "Good sportsmanship reflects character and enhances the debate experience for everyone.",
          examples: [
            "Congratulate opponents on good arguments",
            "Accept losses gracefully",
            "Help others improve",
            "Maintain positive relationships",
          ],
          counter: "Sportsmanship builds community and enhances learning for everyone."
        },
        {
          id: 4,
          title: "Academic Integrity",
          type: "interactive",
          duration: "8 min",
          content: "Academic integrity is fundamental to meaningful debate and education.",
          examples: [
            "Cite sources accurately",
            "Don't plagiarize",
            "Present evidence honestly",
            "Acknowledge limitations",
          ],
          counter: "Academic integrity builds trust and credibility in the debate community."
        },
        {
          id: 5,
          title: "Respect for Opponents",
          type: "interactive",
          duration: "8 min",
          content: "Respecting opponents creates a positive environment and improves debate quality.",
          examples: [
            "Listen to their arguments carefully",
            "Address their points respectfully",
            "Don't make personal attacks",
            "Recognize their strengths",
          ],
          counter: "Respect for opponents enhances debate quality and community."
        },
        {
          id: 6,
          title: "Honest Argumentation",
          type: "interactive",
          duration: "8 min",
          content: "Honest argumentation involves presenting your case truthfully and acknowledging weaknesses.",
          examples: [
            "Present evidence accurately",
            "Acknowledge counter-arguments",
            "Don't misrepresent positions",
            "Admit when you're uncertain",
          ],
          counter: "Honest argumentation builds credibility and advances understanding."
        },
        {
          id: 7,
          title: "Ethics & Sportsmanship Quiz",
          type: "quiz",
          duration: "10 min",
          questions: [
            {
              id: 1,
              question: "What is the primary goal of ethical debate?",
              options: [
                "To make opponents look bad",
                "To impress the judges",
                "To seek truth and understanding",
                "To win at all costs",
              ],
              correct: 2,
              explanation: "The primary goal of ethical debate is to seek truth and understanding, not just to win."
            },
            {
              id: 2,
              question: "What should you do when you lose a debate?",
              options: [
                "Blame the judges",
                "Complain about the topic",
                "Refuse to shake hands",
                "Accept the loss gracefully and learn from it",
              ],
              correct: 3,
              explanation: "Good sportsmanship involves accepting losses gracefully and using them as learning opportunities."
            },
            {
              id: 3,
              question: "Why is academic integrity important in debate?",
              options: [
                "It builds trust and credibility in the debate community",
                "It's not important",
                "It's just a rule to follow",
                "It makes arguments longer",
              ],
              correct: 0,
              explanation: "Academic integrity builds trust and credibility, making debate more meaningful and educational."
            },
            {
              id: 4,
              question: "What is fair play in debate?",
              options: [
                "Being nice to everyone",
                "Ensuring debate remains constructive and educational",
                "Always winning",
                "Following all rules strictly",
              ],
              correct: 1,
              explanation: "Fair play ensures that debate remains a constructive and educational activity for everyone."
            },
            {
              id: 5,
              question: "How should you treat your opponents?",
              options: [
                "As enemies",
                "With respect and recognition of their strengths",
                "With suspicion",
                "With indifference",
              ],
              correct: 1,
              explanation: "Treat opponents with respect and recognize their strengths to create a positive debate environment."
            },
            {
              id: 6,
              question: "What is honest argumentation?",
              options: [
                "Using any means to win",
                "Never admitting mistakes",
                "Always agreeing with opponents",
                "Presenting your case truthfully and acknowledging weaknesses",
              ],
              correct: 3,
              explanation: "Honest argumentation involves presenting your case truthfully and acknowledging weaknesses."
            },
            {
              id: 7,
              question: "What should you do when you're uncertain about a point?",
              options: [
                "Make up an answer",
                "Admit uncertainty and seek clarification",
                "Ignore the point",
                "Attack your opponent instead",
              ],
              correct: 1,
              explanation: "Admit when you're uncertain and seek clarification rather than making up answers."
            },
            {
              id: 8,
              question: "Why is sportsmanship important in debate?",
              options: [
                "It reflects character and enhances the debate experience",
                "It's not important",
                "It's required by rules",
                "It makes you look good",
              ],
              correct: 0,
              explanation: "Sportsmanship reflects character and enhances the debate experience for everyone involved."
            },
          ]
        },
      ]
    },
    10: {
      id: 10,
      title: "Tournament Preparation",
      description: "Complete guide to preparing for debate tournaments including strategy, logistics, and mental preparation.",
      duration: "60 min",
      difficulty: "Advanced",
      xpReward: 400,
      sections: [
        {
          id: 1,
          title: "Tournament Strategy",
          type: "video",
          duration: "10 min",
          content: "Developing a tournament strategy helps you perform consistently and achieve your goals.",
          keyPoints: [
            "Set realistic goals",
            "Plan your approach to each round",
            "Prepare for different opponents",
            "Focus on continuous improvement",
          ]
        },
        {
          id: 2,
          title: "Mental Preparation",
          type: "interactive",
          duration: "10 min",
          content: "Mental preparation is crucial for performing at your best under tournament pressure.",
          examples: [
            "Visualize successful performances",
            "Practice stress management techniques",
            "Develop pre-round routines",
            "Maintain positive mindset",
          ],
          counter: "Mental preparation helps you stay focused and confident during competition."
        },
        {
          id: 3,
          title: "Logistics Planning",
          type: "interactive",
          duration: "8 min",
          content: "Proper logistics planning ensures you can focus on debating without distractions.",
          examples: [
            "Pack all necessary materials",
            "Plan travel and accommodation",
            "Check tournament schedule",
            "Prepare backup plans",
          ],
          counter: "Good logistics planning reduces stress and allows focus on performance."
        },
        {
          id: 4,
          title: "Performance Optimization",
          type: "interactive",
          duration: "10 min",
          content: "Optimizing your performance involves managing energy, focus, and preparation throughout the tournament.",
          examples: [
            "Get adequate sleep",
            "Eat nutritious meals",
            "Stay hydrated",
            "Take breaks between rounds",
          ],
          counter: "Physical and mental well-being directly impact debate performance."
        },
        {
          id: 5,
          title: "Stress Management",
          type: "interactive",
          duration: "8 min",
          content: "Effective stress management helps you perform under pressure and enjoy the tournament experience.",
          examples: [
            "Practice deep breathing",
            "Use positive self-talk",
            "Focus on process, not outcome",
            "Seek support from teammates",
          ],
          counter: "Managing stress improves performance and tournament experience."
        },
        {
          id: 6,
          title: "Team Coordination",
          type: "interactive",
          duration: "8 min",
          content: "For team events, effective coordination maximizes your collective performance.",
          examples: [
            "Establish clear roles",
            "Practice together regularly",
            "Develop communication systems",
            "Support each other",
          ],
          counter: "Team coordination enhances performance and builds camaraderie."
        },
        {
          id: 7,
          title: "Equipment Preparation",
          type: "interactive",
          duration: "6 min",
          content: "Having the right equipment and materials ensures you're prepared for any situation.",
          examples: [
            "Bring backup pens and paper",
            "Pack laptop and charger",
            "Have evidence files ready",
            "Bring snacks and water",
          ],
          counter: "Proper equipment preparation prevents avoidable problems."
        },
        {
          id: 8,
          title: "Tournament Preparation Quiz",
          type: "quiz",
          duration: "10 min",
          questions: [
            {
              id: 1,
              question: "What is the most important aspect of tournament preparation?",
              options: [
                "Setting realistic goals and developing a strategy",
                "Memorizing every possible argument",
                "Having the nicest clothes",
                "Making sure you win every practice round",
              ],
              correct: 0,
              explanation: "Setting realistic goals and developing a strategy is crucial for tournament success."
            },
            {
              id: 2,
              question: "Why is mental preparation important?",
              options: [
                "It helps you perform under pressure and stay focused",
                "It's just a superstition",
                "It's not important",
                "It makes you smarter",
              ],
              correct: 0,
              explanation: "Mental preparation helps you perform under pressure and maintain focus during competition."
            },
            {
              id: 3,
              question: "What should you focus on during a tournament?",
              options: [
                "Process and continuous improvement",
                "How tired you are",
                "Only winning",
                "What other teams are doing",
              ],
              correct: 0,
              explanation: "Focusing on process and continuous improvement leads to better long-term performance."
            },
            {
              id: 4,
              question: "What is the purpose of logistics planning?",
              options: [
                "To ensure you can focus on debating without distractions",
                "To follow tournament rules",
                "To impress other teams",
                "To make tournaments more expensive",
              ],
              correct: 0,
              explanation: "Proper logistics planning ensures you can focus on debating without distractions."
            },
            {
              id: 5,
              question: "How can you optimize your performance during tournaments?",
              options: [
                "Ignore your physical needs",
                "Stay up all night studying",
                "Drink lots of coffee",
                "Get adequate sleep, eat well, and stay hydrated",
              ],
              correct: 3,
              explanation: "Physical and mental well-being directly impact debate performance."
            },
            {
              id: 6,
              question: "What is effective stress management?",
              options: [
                "Getting stressed about stress",
                "Avoiding all stress",
                "Ignoring stress completely",
                "Using techniques to perform under pressure",
              ],
              correct: 3,
              explanation: "Effective stress management helps you perform under pressure and enjoy the tournament experience."
            },
            {
              id: 7,
              question: "Why is team coordination important?",
              options: [
                "It's required by rules",
                "It maximizes collective performance and builds camaraderie",
                "It's not important for individual events",
                "It makes tournaments longer",
              ],
              correct: 1,
              explanation: "Team coordination enhances performance and builds camaraderie in team events."
            },
            {
              id: 8,
              question: "What should you prepare in terms of equipment?",
              options: [
                "Only your laptop",
                "Nothing - tournaments provide everything",
                "Only your clothes",
                "Backup materials, evidence files, and essential supplies",
              ],
              correct: 3,
              explanation: "Prepare backup materials, evidence files, and essential supplies to prevent avoidable problems."
            },
            {
              id: 9,
              question: "What is the purpose of pre-round routines?",
              options: [
                "To waste time",
                "To make you nervous",
                "To follow superstitions",
                "To help you stay focused and confident",
              ],
              correct: 3,
              explanation: "Pre-round routines help you stay focused and confident during competition."
            },
            {
              id: 10,
              question: "How should you approach tournament strategy?",
              options: [
                "Adapt your strategy based on opponents and circumstances",
                "Always use your strongest arguments first",
                "Never change your approach",
                "Use the same approach for every round",
              ],
              correct: 0,
              explanation: "Adapt your tournament strategy based on different opponents and circumstances."
            },
          ]
        },
      ]
    },
    11: {
      id: 11,
      title: "Digital Debate & Online Platforms",
      description: "Master the art of debating in digital environments, including online platforms, video conferencing, and virtual tournaments.",
      duration: "45 min",
      difficulty: "Intermediate",
      xpReward: 350,
      sections: [
        {
          id: 1,
          title: "Online Platform Navigation",
          type: "video",
          duration: "8 min",
          content: "Understanding how to navigate online debate platforms is essential for digital debate success.",
          keyPoints: [
            "Learn platform interfaces",
            "Understand technical requirements",
            "Practice with platform features",
            "Troubleshoot common issues",
          ]
        },
        {
          id: 2,
          title: "Digital Etiquette",
          type: "interactive",
          duration: "8 min",
          content: "Digital etiquette ensures respectful and professional online debate experiences.",
          examples: [
            "Mute when not speaking",
            "Use appropriate language",
            "Respect time limits",
            "Maintain professional appearance",
          ],
          counter: "Digital etiquette creates a positive environment for online debate."
        },
        {
          id: 3,
          title: "Technical Setup",
          type: "interactive",
          duration: "8 min",
          content: "Proper technical setup ensures smooth online debate experiences.",
          examples: [
            "Test internet connection",
            "Check audio and video",
            "Prepare backup devices",
            "Set up proper lighting",
          ],
          counter: "Good technical setup prevents avoidable problems during debates."
        },
        {
          id: 4,
          title: "Virtual Body Language",
          type: "interactive",
          duration: "8 min",
          content: "Virtual body language helps convey confidence and engagement in digital debates.",
          examples: [
            "Maintain eye contact with camera",
            "Use appropriate gestures",
            "Show active listening",
            "Project confidence through posture",
          ],
          counter: "Virtual body language enhances your presence and credibility."
        },
        {
          id: 5,
          title: "Screen Presence",
          type: "interactive",
          duration: "8 min",
          content: "Developing strong screen presence helps you engage audiences in digital debates.",
          examples: [
            "Speak clearly and at good pace",
            "Use facial expressions effectively",
            "Maintain energy and enthusiasm",
            "Connect with virtual audience",
          ],
          counter: "Strong screen presence keeps audiences engaged and interested."
        },
        {
          id: 6,
          title: "Digital Tools",
          type: "interactive",
          duration: "5 min",
          content: "Utilizing digital tools effectively enhances your online debate capabilities.",
          examples: [
            "Use screen sharing for evidence",
            "Utilize chat functions appropriately",
            "Access digital resources quickly",
            "Use presentation tools effectively",
          ],
          counter: "Digital tools can enhance your arguments and presentation."
        },
        {
          id: 7,
          title: "Online Debate Formats",
          type: "interactive",
          duration: "8 min",
          content: "Understanding online debate formats helps you adapt to digital environments.",
          examples: [
            "Adapt traditional formats",
            "Understand time constraints",
            "Use digital features effectively",
            "Maintain format integrity",
          ],
          counter: "Understanding online formats ensures proper debate structure."
        },
        {
          id: 8,
          title: "Digital Debate Quiz",
          type: "quiz",
          duration: "8 min",
          questions: [
            {
              id: 1,
              question: "What is the most important aspect of digital debate preparation?",
              options: [
                "Memorizing arguments",
                "Having the fastest internet",
                "Wearing nice clothes",
                "Proper technical setup and platform familiarity",
              ],
              correct: 3,
              explanation: "Proper technical setup and platform familiarity are crucial for digital debate success."
            },
            {
              id: 2,
              question: "Why is digital etiquette important?",
              options: [
                "It creates a respectful and professional environment",
                "It's just a formality",
                "It makes debates longer",
                "It's not important",
              ],
              correct: 0,
              explanation: "Digital etiquette ensures respectful and professional online debate experiences."
            },
            {
              id: 3,
              question: "What should you do before an online debate?",
              options: [
                "Practice your arguments",
                "Test your internet, audio, video, and platform features",
                "Nothing special",
                "Just show up on time",
              ],
              correct: 1,
              explanation: "Testing technical setup prevents avoidable problems during debates."
            },
            {
              id: 4,
              question: "How can you improve your virtual body language?",
              options: [
                "Move around constantly",
                "Maintain eye contact with camera and use appropriate gestures",
                "Ignore it completely",
                "Always look down",
              ],
              correct: 1,
              explanation: "Virtual body language helps convey confidence and engagement."
            },
            {
              id: 5,
              question: "What is screen presence?",
              options: [
                "Your internet speed",
                "Your camera quality",
                "How big your screen is",
                "Your ability to engage and connect with virtual audiences",
              ],
              correct: 3,
              explanation: "Screen presence is your ability to engage and connect with virtual audiences."
            },
          ]
        },
      ]
    },
    12: {
      id: 12,
      title: "International Debate Styles",
      description: "Explore debate formats and styles from around the world, including British Parliamentary, Asian Parliamentary, and World Schools formats.",
      duration: "75 min",
      difficulty: "Advanced",
      xpReward: 450,
      sections: [
        {
          id: 1,
          title: "British Parliamentary",
          type: "video",
          duration: "12 min",
          content: "British Parliamentary debate is a four-team format with unique roles and speaking order.",
          keyPoints: [
            "Four teams: Government and Opposition",
            "Each team has two speakers",
            "Unique roles for each speaker",
            "Emphasis on clash and engagement",
          ]
        },
        {
          id: 2,
          title: "Asian Parliamentary",
          type: "interactive",
          duration: "12 min",
          content: "Asian Parliamentary debate combines elements of British and American parliamentary styles.",
          examples: [
            "Three speakers per team",
            "Emphasis on logical structure",
            "Clear argument development",
            "Strong rebuttal focus",
          ],
          counter: "Asian Parliamentary emphasizes logical structure and clear argumentation."
        },
        {
          id: 3,
          title: "World Schools",
          type: "interactive",
          duration: "12 min",
          content: "World Schools debate format emphasizes preparation, style, and content balance.",
          examples: [
            "Prepared and impromptu motions",
            "Emphasis on style and content",
            "Three speakers per team",
            "International judging standards",
          ],
          counter: "World Schools format balances preparation with adaptability."
        },
        {
          id: 4,
          title: "International Rules",
          type: "interactive",
          duration: "12 min",
          content: "Understanding international debate rules helps you compete globally.",
          examples: [
            "Time limits and speaking order",
            "Point of information rules",
            "Judging criteria differences",
            "Cultural considerations",
          ],
          counter: "International rules ensure fair and consistent competition."
        },
        {
          id: 5,
          title: "Cultural Differences",
          type: "interactive",
          duration: "12 min",
          content: "Cultural differences influence debate styles and expectations globally.",
          examples: [
            "Communication style variations",
            "Argument structure preferences",
            "Evidence standards",
            "Judging expectations",
          ],
          counter: "Understanding cultural differences enhances international debate success."
        },
        {
          id: 6,
          title: "Global Perspectives",
          type: "interactive",
          duration: "10 min",
          content: "Incorporating global perspectives strengthens your arguments in international debates.",
          examples: [
            "Consider international implications",
            "Use global examples and evidence",
            "Address diverse viewpoints",
            "Respect cultural contexts",
          ],
          counter: "Global perspectives make arguments more relevant and compelling."
        },
        {
          id: 7,
          title: "Format Comparisons",
          type: "interactive",
          duration: "10 min",
          content: "Comparing debate formats helps you adapt your skills across different styles.",
          examples: [
            "Identify common elements",
            "Understand key differences",
            "Adapt strategies accordingly",
            "Develop versatile skills",
          ],
          counter: "Format comparisons help develop versatile debate skills."
        },
        {
          id: 8,
          title: "International Debate Quiz",
          type: "quiz",
          duration: "10 min",
          questions: [
            {
              id: 1,
              question: "How many teams participate in British Parliamentary debate?",
              options: [
                "Six teams",
                "Three teams",
                "Four teams",
                "Two teams",
              ],
              correct: 2,
              explanation: "British Parliamentary debate involves four teams: two Government and two Opposition teams."
            },
            {
              id: 2,
              question: "What is a key feature of Asian Parliamentary debate?",
              options: [
                "Emphasis on logical structure and clear argumentation",
                "No time limits",
                "Only two speakers per team",
                "Only prepared motions",
              ],
              correct: 0,
              explanation: "Asian Parliamentary debate emphasizes logical structure and clear argumentation."
            },
            {
              id: 3,
              question: "What does World Schools debate emphasize?",
              options: [
                "Only style",
                "Only speed",
                "Only content",
                "Balance of style and content",
              ],
              correct: 3,
              explanation: "World Schools debate emphasizes balance between style and content."
            },
            {
              id: 4,
              question: "Why is understanding cultural differences important in international debate?",
              options: [
                "It makes debates longer",
                "It's just a formality",
                "It's not important",
                "It enhances international debate success and communication",
              ],
              correct: 3,
              explanation: "Understanding cultural differences enhances international debate success and communication."
            },
            {
              id: 5,
              question: "What should you consider when incorporating global perspectives?",
              options: [
                "Only your own country",
                "Only the judges' country",
                "Only the motion",
                "International implications and diverse viewpoints",
              ],
              correct: 3,
              explanation: "Incorporating global perspectives involves considering international implications and diverse viewpoints."
            },
          ]
        },
      ]
    },
    13: {
      id: 13,
      title: "Debate Psychology & Mental Game",
      description: "Develop mental resilience, confidence, and psychological strategies for high-pressure debate situations.",
      duration: "50 min",
      difficulty: "Intermediate",
      xpReward: 300,
      sections: [
        {
          id: 1,
          title: "Mental Preparation",
          type: "video",
          duration: "8 min",
          content: "Mental preparation is the foundation for performing at your best in debate situations.",
          keyPoints: [
            "Develop pre-debate routines",
            "Practice visualization techniques",
            "Build mental resilience",
            "Set clear mental goals",
          ]
        },
        {
          id: 2,
          title: "Confidence Building",
          type: "interactive",
          duration: "8 min",
          content: "Building genuine confidence helps you perform better and handle pressure effectively.",
          examples: [
            "Focus on preparation and practice",
            "Celebrate small improvements",
            "Use positive self-talk",
            "Learn from setbacks",
          ],
          counter: "Genuine confidence comes from preparation and positive mindset."
        },
        {
          id: 3,
          title: "Stress Management",
          type: "interactive",
          duration: "8 min",
          content: "Effective stress management helps you perform under pressure and enjoy debate experiences.",
          examples: [
            "Practice deep breathing techniques",
            "Use progressive muscle relaxation",
            "Develop healthy coping strategies",
            "Maintain perspective",
          ],
          counter: "Managing stress improves performance and overall experience."
        },
        {
          id: 4,
          title: "Focus Techniques",
          type: "interactive",
          duration: "8 min",
          content: "Developing focus techniques helps you maintain concentration during debates.",
          examples: [
            "Practice mindfulness",
            "Use attention anchors",
            "Minimize distractions",
            "Stay present in the moment",
          ],
          counter: "Strong focus enhances performance and argument delivery."
        },
        {
          id: 5,
          title: "Emotional Control",
          type: "interactive",
          duration: "8 min",
          content: "Maintaining emotional control helps you think clearly and respond effectively.",
          examples: [
            "Recognize emotional triggers",
            "Practice emotional regulation",
            "Stay calm under pressure",
            "Channel emotions constructively",
          ],
          counter: "Emotional control enables clear thinking and effective responses."
        },
        {
          id: 6,
          title: "Performance Psychology",
          type: "interactive",
          duration: "8 min",
          content: "Understanding performance psychology helps you optimize your debate performance.",
          examples: [
            "Set performance goals",
            "Use mental imagery",
            "Develop performance routines",
            "Monitor mental state",
          ],
          counter: "Performance psychology techniques enhance debate outcomes."
        },
        {
          id: 7,
          title: "Mental Toughness",
          type: "interactive",
          duration: "8 min",
          content: "Developing mental toughness helps you persevere through challenging debate situations.",
          examples: [
            "Embrace challenges",
            "Learn from failures",
            "Maintain determination",
            "Build resilience",
          ],
          counter: "Mental toughness enables persistence and growth."
        },
        {
          id: 8,
          title: "Psychology Quiz",
          type: "quiz",
          duration: "8 min",
          questions: [
            {
              id: 1,
              question: "What is the foundation of mental preparation?",
              options: [
                "Memorizing arguments",
                "Ignoring pressure",
                "Only focusing on winning",
                "Developing routines and visualization techniques",
              ],
              correct: 3,
              explanation: "Mental preparation involves developing routines and visualization techniques."
            },
            {
              id: 2,
              question: "How do you build genuine confidence?",
              options: [
                "By being arrogant",
                "By always winning",
                "Through preparation, practice, and positive mindset",
                "By ignoring your weaknesses",
              ],
              correct: 2,
              explanation: "Genuine confidence comes from preparation, practice, and positive mindset."
            },
            {
              id: 3,
              question: "What is effective stress management?",
              options: [
                "Using techniques to perform under pressure",
                "Getting stressed about stress",
                "Avoiding all stress",
                "Ignoring stress completely",
              ],
              correct: 0,
              explanation: "Effective stress management involves using techniques to perform under pressure."
            },
            {
              id: 4,
              question: "Why is focus important in debate?",
              options: [
                "It enhances performance and argument delivery",
                "It's not important",
                "It makes debates longer",
                "It's just a skill",
              ],
              correct: 0,
              explanation: "Strong focus enhances performance and argument delivery."
            },
            {
              id: 5,
              question: "What is emotional control?",
              options: [
                "Ignoring feelings",
                "Never showing emotions",
                "Maintaining composure and thinking clearly",
                "Being emotionless",
              ],
              correct: 2,
              explanation: "Emotional control involves maintaining composure and thinking clearly."
            },
          ]
        },
      ]
    },
    14: {
      id: 14,
      title: "Advanced Evidence Analysis",
      description: "Learn to critically evaluate, synthesize, and present complex evidence and data in debate contexts.",
      duration: "65 min",
      difficulty: "Advanced",
      xpReward: 500,
      sections: [
        {
          id: 1,
          title: "Data Analysis",
          type: "video",
          duration: "10 min",
          content: "Data analysis skills help you interpret and present statistical evidence effectively in debates.",
          keyPoints: [
            "Understand statistical concepts",
            "Interpret data accurately",
            "Identify data limitations",
            "Present data clearly",
          ]
        },
        {
          id: 2,
          title: "Statistical Evidence",
          type: "interactive",
          duration: "10 min",
          content: "Statistical evidence can strengthen arguments when used appropriately and accurately.",
          examples: [
            "Use relevant statistics",
            "Understand statistical significance",
            "Present data context",
            "Avoid statistical fallacies",
          ],
          counter: "Statistical evidence enhances argument credibility when used properly."
        },
        {
          id: 3,
          title: "Expert Testimony",
          type: "interactive",
          duration: "10 min",
          content: "Expert testimony provides authoritative support for arguments when used effectively.",
          examples: [
            "Verify expert credentials",
            "Use relevant expertise",
            "Present expert opinions accurately",
            "Consider expert bias",
          ],
          counter: "Expert testimony adds credibility when from qualified sources."
        },
        {
          id: 4,
          title: "Source Evaluation",
          type: "interactive",
          duration: "10 min",
          content: "Evaluating sources critically ensures you use reliable and relevant evidence.",
          examples: [
            "Check source credibility",
            "Assess publication quality",
            "Consider source bias",
            "Verify information accuracy",
          ],
          counter: "Source evaluation ensures evidence reliability and relevance."
        },
        {
          id: 5,
          title: "Evidence Synthesis",
          type: "interactive",
          duration: "10 min",
          content: "Synthesizing evidence helps you build comprehensive and compelling arguments.",
          examples: [
            "Combine multiple sources",
            "Identify patterns and trends",
            "Address conflicting evidence",
            "Create coherent narratives",
          ],
          counter: "Evidence synthesis creates stronger, more comprehensive arguments."
        },
        {
          id: 6,
          title: "Fact-Checking",
          type: "interactive",
          duration: "10 min",
          content: "Fact-checking ensures the accuracy and reliability of your evidence.",
          examples: [
            "Verify claims independently",
            "Cross-reference sources",
            "Check for updates",
            "Identify misinformation",
          ],
          counter: "Fact-checking maintains argument integrity and credibility."
        },
        {
          id: 7,
          title: "Evidence Presentation",
          type: "interactive",
          duration: "10 min",
          content: "Effective evidence presentation maximizes the impact of your arguments.",
          examples: [
            "Present evidence clearly",
            "Provide context and explanation",
            "Use visual aids when appropriate",
            "Connect evidence to arguments",
          ],
          counter: "Clear evidence presentation enhances argument effectiveness."
        },
        {
          id: 8,
          title: "Evidence Analysis Quiz",
          type: "quiz",
          duration: "10 min",
          questions: [
            {
              id: 1,
              question: "What is the purpose of data analysis in debate?",
              options: [
                "To confuse opponents",
                "To make arguments longer",
                "To interpret and present statistical evidence effectively",
                "To impress judges",
              ],
              correct: 2,
              explanation: "Data analysis helps interpret and present statistical evidence effectively."
            },
            {
              id: 2,
              question: "How should you use statistical evidence?",
              options: [
                "Always use the biggest numbers",
                "Ignore statistics completely",
                "Use relevant statistics with proper context and understanding",
                "Use any numbers you find",
              ],
              correct: 2,
              explanation: "Statistical evidence should be relevant and presented with proper context."
            },
            {
              id: 3,
              question: "What should you consider when using expert testimony?",
              options: [
                "How famous the expert is",
                "Only the expert's name",
                "Expert credentials, relevance, and potential bias",
                "Only the expert's opinion",
              ],
              correct: 2,
              explanation: "Consider expert credentials, relevance, and potential bias when using expert testimony."
            },
            {
              id: 4,
              question: "Why is source evaluation important?",
              options: [
                "It makes research harder",
                "It ensures evidence reliability and relevance",
                "It's just a formality",
                "It's not important",
              ],
              correct: 1,
              explanation: "Source evaluation ensures evidence reliability and relevance."
            },
            {
              id: 5,
              question: "What is evidence synthesis?",
              options: [
                "Combining multiple sources to create comprehensive arguments",
                "Making up evidence",
                "Ignoring conflicting evidence",
                "Using only one source",
              ],
              correct: 0,
              explanation: "Evidence synthesis involves combining multiple sources to create comprehensive arguments."
            },
          ]
        },
      ]
    },
    15: {
      id: 15,
      title: "Debate Leadership & Team Management",
      description: "Develop leadership skills for debate teams, including coaching, strategy development, and team coordination.",
      duration: "55 min",
      difficulty: "Expert",
      xpReward: 550,
      sections: [
        {
          id: 1,
          title: "Team Leadership",
          type: "video",
          duration: "10 min",
          content: "Effective team leadership maximizes collective performance and creates positive team environments.",
          keyPoints: [
            "Lead by example",
            "Communicate clearly",
            "Build team cohesion",
            "Foster individual growth",
          ]
        },
        {
          id: 2,
          title: "Strategy Development",
          type: "interactive",
          duration: "10 min",
          content: "Developing effective strategies helps teams achieve their goals and perform consistently.",
          examples: [
            "Set clear team goals",
            "Develop preparation strategies",
            "Create performance plans",
            "Adapt strategies as needed",
          ],
          counter: "Effective strategy development enhances team performance and success."
        },
        {
          id: 3,
          title: "Coaching Techniques",
          type: "interactive",
          duration: "10 min",
          content: "Effective coaching helps team members develop their skills and reach their potential.",
          examples: [
            "Provide constructive feedback",
            "Identify individual strengths",
            "Address areas for improvement",
            "Support skill development",
          ],
          counter: "Good coaching accelerates individual and team development."
        },
        {
          id: 4,
          title: "Team Dynamics",
          type: "interactive",
          duration: "10 min",
          content: "Understanding and managing team dynamics creates positive and productive team environments.",
          examples: [
            "Build trust and respect",
            "Manage conflicts constructively",
            "Foster collaboration",
            "Create inclusive environments",
          ],
          counter: "Positive team dynamics enhance performance and satisfaction."
        },
        {
          id: 5,
          title: "Performance Management",
          type: "interactive",
          duration: "10 min",
          content: "Effective performance management helps teams maintain high standards and continuous improvement.",
          examples: [
            "Set performance standards",
            "Monitor team progress",
            "Provide regular feedback",
            "Celebrate achievements",
          ],
          counter: "Performance management maintains high standards and motivation."
        },
        {
          id: 6,
          title: "Communication Skills",
          type: "interactive",
          duration: "10 min",
          content: "Strong communication skills are essential for effective team leadership and coordination.",
          examples: [
            "Listen actively",
            "Communicate clearly and concisely",
            "Provide constructive feedback",
            "Resolve conflicts effectively",
          ],
          counter: "Strong communication enhances team coordination and effectiveness."
        },
        {
          id: 7,
          title: "Leadership Styles",
          type: "interactive",
          duration: "10 min",
          content: "Understanding different leadership styles helps you adapt your approach to different situations.",
          examples: [
            "Democratic leadership",
            "Transformational leadership",
            "Situational leadership",
            "Servant leadership",
          ],
          counter: "Adapting leadership styles enhances team effectiveness."
        },
        {
          id: 8,
          title: "Leadership Quiz",
          type: "quiz",
          duration: "10 min",
          questions: [
            {
              id: 1,
              question: "What is the foundation of effective team leadership?",
              options: [
                "Leading by example and building team cohesion",
                "Making all decisions alone",
                "Always being in charge",
                "Being the best debater",
              ],
              correct: 0,
              explanation: "Effective team leadership involves leading by example and building team cohesion."
            },
            {
              id: 2,
              question: "Why is strategy development important for teams?",
              options: [
                "It makes teams work harder",
                "It helps teams achieve goals and perform consistently",
                "It's just a formality",
                "It's not important",
              ],
              correct: 1,
              explanation: "Strategy development helps teams achieve goals and perform consistently."
            },
            {
              id: 3,
              question: "What is effective coaching?",
              options: [
                "Criticizing mistakes",
                "Ignoring problems",
                "Providing constructive feedback and supporting development",
                "Telling people what to do",
              ],
              correct: 2,
              explanation: "Effective coaching involves providing constructive feedback and supporting development."
            },
            {
              id: 4,
              question: "Why are team dynamics important?",
              options: [
                "They're just social",
                "They create positive and productive team environments",
                "They're not important",
                "They make teams bigger",
              ],
              correct: 1,
              explanation: "Team dynamics create positive and productive team environments."
            },
            {
              id: 5,
              question: "What is performance management?",
              options: [
                "Only focusing on winning",
                "Making people work harder",
                "Ignoring performance issues",
                "Maintaining high standards and continuous improvement",
              ],
              correct: 3,
              explanation: "Performance management involves maintaining high standards and continuous improvement."
            },
          ]
        },
      ]
    },
  };

  const getModuleData = (id) => {
    return modules[id];
  };

  const moduleData = getModuleData(moduleId);
  const currentSectionData = moduleData.sections[currentSection];

  // Shuffle quiz questions only when module/section changes or component mounts
  useEffect(() => {
    if (currentSectionData.type === 'quiz') {
      setShuffledQuestions(currentSectionData.questions.map(q => shuffleQuestionOptions(q)));
    } else {
      setShuffledQuestions(null);
    }
  }, [moduleId, currentSection, currentSectionData.type]);

  useEffect(() => {
    const completed = completedSections.length;
    const total = moduleData.sections.length;
    setProgress((completed / total) * 100);
  }, [completedSections, moduleData.sections.length]);

  const handleSectionComplete = async () => {
    if (!completedSections.includes(currentSectionData.id)) {
      setCompletedSections([...completedSections, currentSectionData.id]);
    }
    
    if (currentSection < moduleData.sections.length - 1) {
      setCurrentSection(currentSection + 1);
      setQuizSubmitted(false);
      setQuizScore(null);
    } else {
      // Module completed - ensure user context is up to date before navigating
      console.log('🎯 Module completed, refreshing user data...');
      try {
        // Refresh user data from backend
        await loadUser();
        console.log('✅ User data refreshed successfully');
        
        // Add a small delay to ensure context updates
        await new Promise(resolve => setTimeout(resolve, 500));
        console.log('⏳ Delay completed');
      } catch (err) {
        console.error('❌ Error refreshing user data:', err);
      }
      console.log('🚀 Navigating to training page...');
      navigate('/training');
    }
  };

  const handleQuizSubmit = async () => {
    const correctAnswers = currentSectionData.questions.filter((question) => 
      quizAnswers[question.id] === question.correct
    ).length;
    
    const score = (correctAnswers / currentSectionData.questions.length) * 100;
    setQuizScore(score);
    setQuizSubmitted(true);
    
    await updateModuleProgress(moduleId, score, timeSpent);
    
    if (score >= 70) {
      setTimeout(() => {
        handleSectionComplete();
      }, 3000);
    }
  };

  const handleQuizRetry = () => {
    setQuizAnswers({});
    setQuizSubmitted(false);
    setQuizScore(null);
    // Re-shuffle questions for retry
    if (currentSectionData.type === 'quiz') {
      setShuffledQuestions(currentSectionData.questions.map(q => shuffleQuestionOptions(q)));
    }
  };

  const renderSectionContent = (shuffledQuestionsOverride) => {
    switch (currentSectionData.type) {
      case 'video':
        return (
          <div className="space-y-6">
            <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-8 text-center">
              <Play className="w-16 h-16 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
              <p className="text-gray-600 dark:text-gray-300">Video content would be embedded here</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Duration: {currentSectionData.duration}</p>
            </div>
            <div className="prose max-w-none">
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Key Points:</h3>
              <ul className="space-y-2">
                {currentSectionData.keyPoints.map((point, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700 dark:text-gray-200">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );
      
      case 'interactive':
        return (
          <div className="space-y-6">
            <div className="prose max-w-none">
              <p className="text-lg text-gray-700 dark:text-gray-200 mb-6">{currentSectionData.content}</p>
              
              <h4 className="text-lg font-semibold mb-4 flex items-center text-gray-900 dark:text-white">
                <Lightbulb className="w-5 h-5 mr-2 text-yellow-600 dark:text-yellow-400" />
                Examples:
              </h4>
              <div className="space-y-4">
                {currentSectionData.examples.map((example, index) => (
                  <div key={index} className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border-l-4 border-gray-300 dark:border-gray-600">
                    <p className="text-gray-700 dark:text-gray-200 italic">"{example}"</p>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg border border-blue-200 dark:border-blue-600">
              <h4 className="font-semibold text-blue-900 dark:text-blue-200 mb-2 flex items-center">
                <Target className="w-4 h-4 mr-2" />
                How to Counter:
              </h4>
              <p className="text-blue-800 dark:text-blue-200">{currentSectionData.counter}</p>
            </div>
          </div>
        );
      
      case 'quiz': {
        const questionsToRender = shuffledQuestionsOverride || shuffledQuestions || currentSectionData.questions;
        return (
          <div className="space-y-6">
            <div className="bg-yellow-50 dark:bg-yellow-900/30 p-4 rounded-lg border border-yellow-200 dark:border-yellow-600">
              <h4 className="font-semibold text-yellow-900 dark:text-yellow-200 mb-2 flex items-center">
                <Award className="w-4 h-4 mr-2" />
                Final Quiz
              </h4>
              <p className="text-yellow-800 dark:text-yellow-200">
                Test your understanding. You need 70% to pass and complete this module.
              </p>
            </div>
            {!quizSubmitted ? (
              <div className="space-y-6">
                {questionsToRender.map((question, index) => (
                  <div key={question.id} className="card bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                    <h4 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
                      Question {index + 1}: {question.question}
                    </h4>
                    <div className="space-y-3">
                      {question.options.map((option, optionIndex) => (
                        <label key={optionIndex} className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type="radio"
                            name={`question-${question.id}`}
                            value={optionIndex}
                            onChange={() => setQuizAnswers({...quizAnswers, [question.id]: optionIndex})}
                            className="text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-gray-800 dark:text-gray-100 font-medium">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
                <button
                  onClick={handleQuizSubmit}
                  className="btn-primary w-full"
                  disabled={Object.keys(quizAnswers).length < currentSectionData.questions.length}
                >
                  Submit Quiz
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                <div className={`p-6 rounded-lg ${quizScore >= 70 ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-600' : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-600'}`}>
                  <h4 className={`font-semibold mb-2 ${quizScore >= 70 ? 'text-green-900' : 'text-red-900'}`}>
                    Quiz Score: {quizScore}%
                  </h4>
                  <p className={quizScore >= 70 ? 'text-green-800' : 'text-red-800'}>
                    {quizScore >= 70 ? 'Congratulations! You passed the quiz.' : 'You need 70% to pass. Please try again.'}
                  </p>
                </div>
                {/* Quiz Results */}
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Quiz Results:</h4>
                  {currentSectionData.questions.map((question, index) => {
                    const userAnswer = quizAnswers[question.id];
                    const isCorrect = userAnswer === question.correct;
                    return (
                      <div key={question.id} className={`p-4 rounded-lg border ${
                        isCorrect ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-600' : 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-600'
                      }`}>
                        <div className="flex items-start space-x-3">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                            isCorrect ? 'bg-green-500' : 'bg-red-500'
                          }`}>
                            {isCorrect ? '✓' : '✗'}
                          </div>
                          <div className="flex-1">
                            <h5 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                              Question {index + 1}: {question.question}
                            </h5>
                            <div className="space-y-1">
                              {question.options.map((option, optionIndex) => (
                                <div key={optionIndex} className={`text-sm font-medium ${
                                  optionIndex === question.correct 
                                    ? 'text-green-700 dark:text-green-300'
                                    : optionIndex === userAnswer && !isCorrect
                                    ? 'text-red-700 dark:text-red-300'
                                    : 'text-gray-700 dark:text-gray-200'
                                }`}>
                                  {optionIndex === question.correct && '✓ '}
                                  {optionIndex === userAnswer && !isCorrect && '✗ '}
                                  {option}
                                </div>
                              ))}
                            </div>
                            {!isCorrect && (
                              <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/30 rounded border border-blue-200 dark:border-blue-600">
                                <p className="text-sm text-blue-800 dark:text-blue-200">
                                  <strong>Explanation:</strong> {question.explanation}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {quizScore < 70 && (
                  <button onClick={handleQuizRetry} className="btn-primary">
                    Retry Quiz
                  </button>
                )}
              </div>
            )}
          </div>
        );
      }
      
      default:
        return <div>Content not available</div>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-24 pb-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/training')}
            className="flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Training
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{moduleData.title}</h1>
              <p className="text-gray-600 dark:text-gray-300">{moduleData.description}</p>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500 dark:text-gray-400">XP Reward</div>
              <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400 flex items-center">
                <Zap className="w-5 h-5 mr-1" />
                {moduleData.xpReward}
              </div>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Progress: {Math.round(progress)}%
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Section {currentSection + 1} of {moduleData.sections.length}
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className="bg-primary-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Section Navigation */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
              disabled={currentSection === 0}
              className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </button>
            
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {currentSectionData.title}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {currentSectionData.duration}
              </p>
            </div>
            
            <div className="w-20"></div> {/* Spacer to maintain centering */}
          </div>
        </div>

        {/* Content */}
        <div className="card">
          {renderSectionContent(shuffledQuestions)}
        </div>

        {/* Section Completion */}
        {currentSectionData.type !== 'quiz' && (
          <div className="mt-6 text-center">
            <button
              onClick={handleSectionComplete}
              className="btn-primary"
            >
              {currentSection === moduleData.sections.length - 1 ? 'Complete Module' : 'Complete Section'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainingModule; 